import { createLinkElement, ensureSafeUrl } from '@ckeditor/ckeditor5-link/src/utils'
import { findOptimalInsertionRange } from '@ckeditor/ckeditor5-widget/src/utils';

/**
 * Creates a regular expression used to test for files.
 */
export function createFileTypeRegExp(types) {
    // Sanitize the MIME type name which may include: "+", "-" or ".".
    const regExpSafeNames = types.map(type => type.replace('+', '\\+'));
    return new RegExp(`^application\\/${ (types.includes('*') ? '.+' : `(${regExpSafeNames.join('|')})`) }$`);
}

/**
 * Creates a promise that fetches the file local source (Base64 or blob) and resolves with a `File` object.
 *
 * @param link  File whose source to fetch.
 * @returns A promise which resolves when an file source is fetched and converted to a `File` instance.
 * It resolves with a `File` object. If there were any errors during file processing, the promise will be rejected.
 */
export function fetchLocalFile(link ) {
    return new Promise((resolve, reject) => {
        const linkHref = link .getAttribute('href');
        // Fetch works asynchronously and so does not block browser UI when processing data.
        fetch(linkHref)
            .then(resource => resource.blob())
            .then(blob => {
            const mimeType = getFileMimeType(blob, linkHref);
            const ext = mimeType.replace('file/', '');
            const filename = `file.${ext}`;
            const file = new File([blob], filename, { type: mimeType });
            resolve(file);
        })
        .catch(err => {
            return reject(err);
        });
    });
}

/**
 * Checks whether a given node is an file element with a local source (Base64 or blob).
 *
 * @param node The node to check.
 */
export function isLocalFile(node) {
    if (!!!node || (!!node && (!node.is( 'element', 'a' ) || !node.getAttribute('href')))) {
        return false;
    }

    return !!node.getAttribute( 'href' );
}

/**
 * Extracts an file type based on its blob representation or its source.
 * @param blob File blob representation.
 * @param src File `src` attribute value.
 */
export function getFileMimeType( blob, src ) {
	if ( blob.type ) {
		return blob.type;
	} else if ( src.match( /data:(image\/\w+);base64/ ) ) {
		return src.match( /data:(image\/\w+);base64/ )[ 1 ].toLowerCase();
	} else {
		throw new Error('Could not retrieve mime type for file.');
	}
}

/**
 * Handles inserting single file.
 * method.
 *
 * @param attributes Attributes of the inserted link.
 * This method filters out the attributes which are disallowed by the {@link module:engine/model/schema~Schema}.
 * @param selectable Place to insert the link.
 *
 * @return The inserted model link element.
 */
export function insertFileLink(editor, file, attributes = {}, selectable = null, options = {}) {
    const model = editor.model;
    const selection = model.document.selection;
    // Mix declarative attributes with selection attributes because the new image should "inherit"
    // the latter for best UX. For instance, inline images inserted into existing links
    // should not split them. To do that, they need to have "linkHref" inherited from the selection.
    attributes = {
        ...Object.fromEntries(selection.getAttributes()),
        ...attributes
    };

    return model.change(writer => {
        // If selection has non-collapsed ranges, we change attribute on nodes inside those ranges
        // omitting nodes where the `linkHref` attribute is disallowed.
        const ranges = model.schema.getValidRanges(selection.getRanges(), 'linkHref');
        // But for the first, check whether the `linkHref` attribute is allowed on selected blocks (e.g. the "image" element).
        const allowedRanges = [];
        for (const element of selection.getSelectedBlocks()) {
            if (model.schema.checkAttribute(element, 'linkHref')) {
                allowedRanges.push(writer.createRangeOn(element));
            }
        }
        // Ranges that accept the `linkHref` attribute. Since we will iterate over `allowedRanges`, let's clone it.
        const rangesToUpdate = allowedRanges.slice();
        // For all selection ranges we want to check whether given range is inside an element that accepts the `linkHref` attribute.
        // If so, we don't want to propagate applying the attribute to its children.
        for (const range of ranges) {
            // if (this._isRangeToUpdate(range, allowedRanges)) {
                rangesToUpdate.push(range);
            // }
        }
        if(rangesToUpdate.length>0) {
            for (const range of rangesToUpdate) {
                let linkRange = range;
                if (rangesToUpdate.length === 1) {
                    // Current text of the link in the document.
                    const linkText = extractTextFromSelection(selection);
                    // if (selection.getAttribute('linkHref') === linkText) {
                        linkRange = updateLinkContent(model, writer, range, attributes, linkText);
                        writer.setSelection(writer.createSelection(linkRange));
                    // }
                }
                writer.setAttribute('linkHref', '', linkRange);
            }
        }else{
            const position = findOptimalInsertionRange( selectable || selection, model );

            const linkText = writer.createText(file?.name, attributes);

            const { end: positionAfter } = model.insertContent( linkText, position );

            // Put the selection at the end of the inserted link.
            // Using end of range returned from insertContent in case nodes with the same attributes got merged.
            writer.setSelection( positionAfter );
        }
    });
}

// Returns a text of a link under the collapsed selection or a selection that contains the entire link.
function extractTextFromSelection(selection) {
    if (selection.isCollapsed) {
        const firstPosition = selection.getFirstPosition();
        return firstPosition.textNode && firstPosition.textNode.data;
    }
    else {
        const rangeItems = Array.from(selection.getFirstRange().getItems());
        if (rangeItems.length > 1) {
            return null;
        }
        const firstNode = rangeItems[0];
        if (firstNode.is('$text') || firstNode.is('$textProxy')) {
            return firstNode.data;
        }
        return null;
    }
}

/**
 * Updates selected link with a new value as its content and as its href attribute.
 *
 * @param model Model is need to insert content.
 * @param writer Writer is need to create text element in model.
 * @param range A range where should be inserted content.
 * @param attributes Attributes of the inserted link.
 * @param displayText Text of the inserted link.
 */
function updateLinkContent(model, writer, range, attributes, displayText) {
    const text = writer.createText(displayText, attributes);
    return model.insertContent(text, range);
}